/**
 * The price sheet the operator holds while they talk.
 *
 * NOT PART OF THE SCRIPT AND NOT SPOKEN FROM. The script's own lines carry no
 * numbers at all - the prompt forbids them and the schema has nowhere to put
 * one - because a price a model invented is a price somebody says out loud. This
 * is the real ladder, read from the plan catalogue at the moment the script was
 * written, so a plan whose price changed last month is not quoted from a stored
 * copy.
 *
 * THE ONE-OFF FEES ARE MARKED AS PROPOSALS, in the row itself rather than in a
 * footnote. `muugijuhis.md` section 14.4 still has no date on it, and a
 * salesperson who quotes a proposal as final has given away a discount they
 * cannot take back.
 */
import { StatusPill } from "../../../../ui";
import type { T } from "../../../site-scanner/types";
import type { BrainPackage } from "../../../../../lib/siteBrainScript";

export function PackageLadder({
  packages,
  lead,
  t,
}: {
  packages: BrainPackage[];
  /** The rung the fit verdict points at. A suggestion; all four are shown. */
  lead: string | null;
  t: T;
}) {
  if (!packages.length) return null;

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="text-sm font-semibold">{t("brain.script.packages")}</h3>
        <span className="text-xs text-muted-foreground">{t("brain.script.packagesHint")}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[30rem] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-1 pr-3 font-medium">{t("brain.script.package")}</th>
              <th className="py-1 pr-3 font-medium">{t("brain.script.setup")}</th>
              <th className="py-1 pr-3 font-medium">{t("brain.script.monthly")}</th>
              <th className="py-1 font-medium">{t("brain.script.forWhom")}</th>
            </tr>
          </thead>
          <tbody>
            {packages.map((rung) => (
              <tr key={rung.planCode} className="border-t border-border align-top">
                <td className="py-2 pr-3">
                  <span className="font-medium">{rung.label}</span>
                  {rung.label === lead ? (
                    <StatusPill tone="ok">{t("brain.script.leadWith")}</StatusPill>
                  ) : null}
                </td>
                <td className="py-2 pr-3 tabular-nums">
                  {rung.setupEur > 0 ? `${rung.setupEur} EUR` : t("brain.script.noSetup")}
                  {rung.proposed && rung.setupEur > 0 ? (
                    <span className="block text-xs text-muted-foreground">{t("brain.script.proposed")}</span>
                  ) : null}
                </td>
                <td className="py-2 pr-3 tabular-nums">
                  {/* `eur` and not `n`: these are prices, not counts. A variable
                      called `n` is what the catalogue test pluralises on, and a
                      price has no noun to agree with. */}
                  {t("brain.script.perMonth", { eur: rung.monthlyEur })}
                  {rung.yearlyMonthlyEur !== null ? (
                    <span className="block text-xs text-muted-foreground">
                      {t("brain.script.yearly", { eur: rung.yearlyMonthlyEur })}
                    </span>
                  ) : null}
                </td>
                <td className="py-2 text-muted-foreground">{rung.fit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
